import { Upload as AntdUpload } from 'antd'
import React, { useCallback } from 'react'
import { useLingui } from '@lingui/react/macro'

import { getS3UploadUrl, uploadToS3 } from './services/upload'

import type { UploadProps as AntdUploadProps } from 'antd'
import type { RcFile } from 'antd/es/upload'
import type { UploadRef } from 'antd/lib/upload/Upload'

const { Dragger } = AntdUpload

export interface IFileResponse {
  filename: string
  url: string
  accessURL: string
}

export interface UploadProps extends Omit<AntdUploadProps, 'name'> {
  customGetS3UploadUrl?: typeof getS3UploadUrl
  afterSuccess?: (data: IFileResponse) => void
  onUploadError?: () => void
  onUploadStart?: (abortController: AbortController) => void // 修改：传递 AbortController 而不是 XMLHttpRequest
  uploadType?: string
  uploadRef?: React.RefObject<UploadRef>
  /** 是否使用拖拽上传，默认为false */
  isDragger?: boolean
  /** 是否抑制取消上传的错误消息，默认为false */
  suppressAbortMessage?: boolean
}

export default function Upload(props: UploadProps) {
  const {
    customGetS3UploadUrl,
    afterSuccess,
    onUploadError,
    onUploadStart,
    uploadType = 'image',
    uploadRef,
    isDragger = true,
    suppressAbortMessage = false,
    ...restUploadProps
  } = props

  const { t } = useLingui()

  const mergedLocale = useCallback(() => {
    return {
      forbidFileType: t`Forbid file type`,
      fileUploadAborted: t`File upload aborted`,
      unknownError: t`Unknown error`,
      uploadFailed: t`File upload failed. Please try again.`,
    }
  }, [])

  const defaultCustomRequest: AntdUploadProps['customRequest'] = async (
    options,
  ) => {
    const { filename: name, file, onSuccess, onError, onProgress } = options

    // 创建 AbortController 来管理整个上传流程
    const abortController = new AbortController()

    const handleError = (event: any, body?: any) => {
      // 如果是取消上传且设置了抑制消息，则不调用onError
      if (
        suppressAbortMessage &&
        event?.message === mergedLocale().fileUploadAborted
      ) {
        onUploadError?.()
        return
      }
      onError?.(event, body)
      onUploadError?.()
    }

    if (!(file instanceof Blob)) {
      handleError(new Error(mergedLocale().forbidFileType))
      return
    }

    const filename = (file as RcFile).name ?? name

    try {
      // 暴露 AbortController 给外部，允许中断整个上传流程
      onUploadStart?.(abortController)

      const uploadUrlData = await (customGetS3UploadUrl || getS3UploadUrl)({
        payload: { filename, type: uploadType },
        signal: abortController.signal, // 传递 signal 以支持中断
      })

      // 检查是否已被中断
      if (abortController.signal.aborted) {
        handleError(new Error(mergedLocale().fileUploadAborted))
        return
      }

      const xhr = uploadToS3(uploadUrlData.sign, file, {
        onSuccess(xhr) {
          // s3 file url
          const url = uploadUrlData.accessURL
          // s3 response
          const response = {
            filename,
            url,
            accessURL: uploadUrlData.accessURL,
          }
          afterSuccess?.(response)
          onSuccess?.(JSON.stringify(response), xhr)
        },
        onError(error) {
          handleError(error)
        },
        onAbort() {
          handleError(new Error(mergedLocale().fileUploadAborted))
        },
        onProgress(value) {
          onProgress?.({
            percent: value,
          })
        },
        errorMessage: mergedLocale().uploadFailed,
      })

      // 监听 AbortController，如果被中断则中断 xhr
      abortController.signal.addEventListener('abort', () => {
        xhr.abort()
      })
    } catch (err) {
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          handleError(new Error(mergedLocale().fileUploadAborted))
        } else {
          handleError(err)
        }
      } else {
        handleError(new Error(mergedLocale().unknownError))
      }
    }
  }

  const UploadComponent = isDragger ? Dragger : AntdUpload

  return (
    <UploadComponent
      ref={uploadRef}
      customRequest={defaultCustomRequest}
      {...restUploadProps}
    />
  )
}
